/**
 * 作業手順・検査（製造品目） — 2026-11-05。
 * 技術・管理者が直し、承認で「今の版」になる。ファイルはドキュメントに入る。
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { dmsApi } from '@/pages/docs/dmsApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mfg`;
export const CHECK_TYPES = [['pass_fail', '合否'], ['numeric', '数値'], ['photo', '写真']];
export const FREQS = [['first', '初品'], ['every_n', 'N個ごと'], ['each', '毎個']];

const emptyStep = () => ({ title: '', body: '', point: '', media: [] });
const emptyCheck = () => ({ name: '', type: 'pass_fail', required: true, frequency: 'first',
  every_n: 10, uom: '', method: '', target: '', lower: '', upper: '', tolerance: '' });

export const ItemInstructions = ({ item }) => {
  const [data, setData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [checks, setChecks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/items/${item.id}/instructions`);
    setData(r.data);
    const editable = r.data.draft || r.data.current;
    setSteps((editable?.steps || []).map((s) => ({ ...s })));
    setChecks((editable?.checks || []).map((c) => ({ ...c })));
    setDocs(editable?.docs || []);
  }, [item.id]);

  useEffect(() => { load().catch(() => toast.error('作業手順書を読めませんでした')); }, [load]);

  const upload = async (file, onDone) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', `${item.sku} 作業手順`);
      fd.append('doc_type', 'procedure');
      if (data?.folder_id) fd.append('folder_id', data.folder_id);
      const r = await dmsApi.uploadLinked('mfg_item', item.id, fd);
      const d = r?.document || {};
      onDone({ document_id: d.id, title: d.title || file.name, source: 'upload',
        kind: /video/i.test(file.type) ? 'video' : /pdf/i.test(file.type) ? 'pdf' : 'photo' });
      toast.success('ドキュメントに保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'アップロードできませんでした');
    } finally { setBusy(false); }
  };

  const chooseDoc = async (onDone) => {
    const q = window.prompt('ドキュメントから選ぶ — 書類名を入れてください');
    if (!q || !q.trim()) return;
    try {
      const r = await dmsApi.documents({ q: q.trim(), limit: 5 });
      const hit = (r.documents || r.rows || r || [])[0];
      if (!hit) return toast.error('見つかりませんでした');
      onDone({ document_id: hit.id, title: hit.title || hit.filename, source: 'document',
        kind: /pdf/i.test(hit.mime_type || '') ? 'pdf' : /video/i.test(hit.mime_type || '') ? 'video' : 'photo' });
      toast.success(`「${hit.title || hit.filename}」を紐付けました（複製しません）`);
    } catch (_e) { toast.error('ドキュメントを探せませんでした'); }
  };

  const save = async () => {
    const body = {
      steps: steps.filter((s) => s.title.trim()).map((s) => ({
        title: s.title, body: s.body || null, point: s.point || null,
        media: (s.media || []).map((m) => ({ ...m })) })),
      checks: checks.filter((c) => c.name.trim()).map((c) => ({
        ...c, target: c.target === '' ? null : Number(c.target),
        lower: c.lower === '' ? null : Number(c.lower),
        upper: c.upper === '' ? null : Number(c.upper),
        tolerance: c.tolerance === '' ? null : Number(c.tolerance),
        every_n: c.frequency === 'every_n' ? Number(c.every_n || 1) : null })),
      docs: docs.map((d) => ({ ...d })),
    };
    if (!body.steps.length) return toast.error('手順を1つ以上入れてください');
    setBusy(true);
    try {
      if (data?.draft) await axios.put(`${API}/instructions/${data.draft.id}`, body);
      else await axios.post(`${API}/items/${item.id}/instructions`, body);
      toast.success('下書きを保存しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  const approve = async () => {
    try {
      await axios.post(`${API}/instructions/${data.draft.id}/approve`);
      toast.success('承認しました');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '承認できませんでした'); }
  };

  if (!data) return <p className="p-4 text-sm text-zinc-500">読み込み中...</p>;
  const cur = data.current;
  const canEdit = data.can_edit;

  return (
    <div className="space-y-4" data-testid="item-instructions">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
        {cur ? (
          <>
            <Badge className="bg-emerald-600" data-testid="item-instructions-current">
              rev {cur.rev} ・ 承認済み
            </Badge>
            <span className="text-sm text-zinc-600">
              承認 {cur.approved_by} ・ {(cur.approved_at || '').slice(0, 10)}
            </span>
          </>
        ) : (
          <span className="text-sm font-semibold text-amber-700"
            data-testid="item-instructions-none">作業手順書は未登録です</span>
        )}
        {data.draft && (
          <Badge variant="outline" className="border-amber-400 text-amber-800"
            data-testid="item-instructions-draft">rev {data.draft.rev} ・ 下書き</Badge>
        )}
        {canEdit && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={save} disabled={busy}
              data-testid="item-instructions-save">
              {data?.draft ? '下書きを保存' : '下書きを作る'}
            </Button>
            {data.draft && (
              <Button size="sm" onClick={approve} data-testid="item-instructions-approve">承認</Button>
            )}
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs font-semibold text-zinc-700">手順書PDF・動画</Label>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {docs.map((d, i) => (
            <Badge key={`${d.document_id}-${i}`} variant="outline" data-testid={`item-instructions-doc-${i}`}>
              {d.title}{d.source === 'document' ? '（ドキュメント参照）' : ''}
            </Badge>
          ))}
          {canEdit && (<>
            <Input type="file" className="h-8 max-w-[14rem] text-xs"
              onChange={(e) => upload(e.target.files?.[0], (m) => setDocs((p) => [...p, m]))}
              data-testid="item-instructions-doc-upload" />
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => chooseDoc((m) => setDocs((p) => [...p, m]))}
              data-testid="item-instructions-doc-choose">ドキュメントから選ぶ</Button>
          </>)}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-zinc-700">手順</p>
        {steps.map((s, i) => (
          <div key={s.id || i} className="rounded-lg border border-zinc-200 p-2"
            data-testid={`item-instructions-step-${i}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-zinc-400">{i + 1}</span>
              <Input value={s.title} disabled={!canEdit} placeholder="タイトル"
                onChange={(e) => setSteps((p) => p.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                data-testid={`item-instructions-step-title-${i}`} />
              {canEdit && (
                <button className="text-xs text-rose-600" onClick={() => setSteps((p) => p.filter((_, j) => j !== i))}
                  data-testid={`item-instructions-step-del-${i}`}>削除</button>
              )}
            </div>
            <Textarea rows={2} className="mt-1" value={s.body || ''} disabled={!canEdit} placeholder="説明"
              onChange={(e) => setSteps((p) => p.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} />
            <Input className="mt-1" value={s.point || ''} disabled={!canEdit} placeholder="ポイント（注意）"
              onChange={(e) => setSteps((p) => p.map((x, j) => (j === i ? { ...x, point: e.target.value } : x)))} />
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {(s.media || []).map((m, k) => (
                <Badge key={`${m.document_id}-${k}`} variant="outline" className="text-[11px]">
                  {m.title || m.kind}
                </Badge>
              ))}
              {canEdit && (<>
                <Input type="file" className="h-8 max-w-[12rem] text-xs"
                  onChange={(e) => upload(e.target.files?.[0], (m) => setSteps((p) => p.map((x, j) => (
                    j === i ? { ...x, media: [...(x.media || []), m] } : x))))}
                  data-testid={`item-instructions-step-upload-${i}`} />
                <Button size="sm" variant="outline" className="h-8 text-[11px]"
                  onClick={() => chooseDoc((m) => setSteps((p) => p.map((x, j) => (
                    j === i ? { ...x, media: [...(x.media || []), m] } : x))))}
                  data-testid={`item-instructions-step-choose-${i}`}>ドキュメントから選ぶ</Button>
              </>)}
            </div>
          </div>
        ))}
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setSteps((p) => [...p, emptyStep()])}
            data-testid="item-instructions-add-step">手順を追加</Button>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-zinc-700">検査項目</p>
        {checks.map((c, i) => (
          <div key={c.id || i} className="grid gap-2 rounded-lg border border-zinc-200 p-2 sm:grid-cols-4"
            data-testid={`item-instructions-check-${i}`}>
            <Input value={c.name} disabled={!canEdit} placeholder="名前"
              onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
              data-testid={`item-instructions-check-name-${i}`} />
            <select value={c.type} disabled={!canEdit}
              onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))}
              className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm"
              data-testid={`item-instructions-check-type-${i}`}>
              {CHECK_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={c.frequency} disabled={!canEdit}
              onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, frequency: e.target.value } : x)))}
              className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm"
              data-testid={`item-instructions-check-freq-${i}`}>
              {FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input value={c.method || ''} disabled={!canEdit} placeholder="方法（例: ノギス）"
              onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, method: e.target.value } : x)))} />
            {c.type === 'numeric' && (
              <>
                <Input value={c.target ?? ''} disabled={!canEdit} placeholder="基準値" type="number"
                  onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))}
                  data-testid={`item-instructions-check-target-${i}`} />
                <Input value={c.lower ?? ''} disabled={!canEdit} placeholder="下限" type="number"
                  onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, lower: e.target.value } : x)))} />
                <Input value={c.upper ?? ''} disabled={!canEdit} placeholder="上限" type="number"
                  onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, upper: e.target.value } : x)))} />
                <Input value={c.uom || ''} disabled={!canEdit} placeholder="単位"
                  onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, uom: e.target.value } : x)))} />
              </>
            )}
            {c.frequency === 'every_n' && (
              <Input value={c.every_n ?? ''} disabled={!canEdit} placeholder="N" type="number"
                onChange={(e) => setChecks((p) => p.map((x, j) => (j === i ? { ...x, every_n: e.target.value } : x)))}
                data-testid={`item-instructions-check-n-${i}`} />
            )}
          </div>
        ))}
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setChecks((p) => [...p, emptyCheck()])}
            data-testid="item-instructions-add-check">検査項目を追加</Button>
        )}
      </div>

      {(data.revs || []).length > 1 && (
        <p className="text-[11px] text-zinc-500" data-testid="item-instructions-revs">
          版: {data.revs.map((r) => `rev ${r.rev}(${r.status_label})`).join(' / ')}
        </p>
      )}
    </div>
  );
};
